import { Controller } from "@hotwired/stimulus"
import { Offcanvas } from "bootstrap"
import TableFrameNavigator from "../utils/table_frame_navigator"
import TableQueryState from "../utils/table_query_state"

// Declarative navigation for filter forms. Mark inputs as `field` targets and
// provide the managed URL parameter names so unrelated query state is retained.
//
// When the controller lives inside a Bootstrap offcanvas (the contacts letter
// sheet), a filter change first closes the sheet through Bootstrap's public
// API and navigates from `hidden.bs.offcanvas`, so the focus trap, backdrop and
// body scroll lock are released by Bootstrap itself before Turbo replaces the
// markup. Never reach into `_focustrap` or other private members.
export default class extends Controller {
  static targets = ["field"]
  static values = {
    frameId: String,
    tableKey: String,
    managedParams: Array,
    searchParam: { type: String, default: "search" },
    searchDelay: { type: Number, default: 250 },
    restoreFocus: String
  }

  connect() {
    this.navigator = new TableFrameNavigator({
      frameId: this.frameIdValue,
      tableKey: this.tableKeyValue || null,
      onPopState: (queryState) => this.hydrate(queryState)
    })
    this.hydrate()
    if (this.#sheet()) {
      // A page cached with the sheet open would restore its backdrop and
      // scroll lock but no Bootstrap instance; close it before the snapshot.
      this.boundBeforeCache = () => this.#settleSheet()
      document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    }
  }

  disconnect() {
    this.navigator?.disconnect()
    if (this.boundBeforeCache) document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    this.#settleSheet()
  }

  #settleSheet() {
    const sheet = this.#sheet()
    if (!sheet) return
    // The frame is being replaced: release the backdrop and scroll lock for a
    // sheet that is still open, and drop Bootstrap's instance for the element
    // that is about to be detached so it does not retain the old subtree.
    // Disposing while a show/hide transition is pending crashes Bootstrap's
    // own transition callback, so a moving sheet is disposed on `hidden`
    // (Bootstrap emulates transitionend on detached elements).
    const instance = Offcanvas.getInstance(sheet)
    if (!instance) return
    if (this.#sheetMoving(sheet)) {
      sheet.addEventListener("hidden.bs.offcanvas", () => instance.dispose(), { once: true })
      this.#hideSheet(sheet, instance)
    } else {
      instance.dispose()
    }
  }

  apply() {
    this.#navigate(this.#restoreFocusSelector())
  }

  search() {
    // The search field lives inside the frame that is about to be replaced;
    // without a restore target the caret is lost the moment results arrive.
    this.navigator.scheduleSearch(this.#queryState(), {
      delay: this.searchDelayValue,
      preserveScroll: { x: true, y: false },
      restoreFocusSelector: this.#restoreFocusSelector()
    })
  }

  clear() {
    this.fieldTargets.forEach((field) => this.#clearField(field))
    this.apply()
  }

  remove(event) {
    const param = event.currentTarget.dataset.tableFilterNavigationParam
    if (!param) return
    this.fieldTargets.filter((field) => this.#paramName(field) === param).forEach((field) => this.#clearField(field))
    // The chip that was clicked disappears with the re-render, so it cannot be
    // the focus target; the chip may name where focus should land instead.
    this.#navigate(event.currentTarget.dataset.tableFilterNavigationRestoreFocus || this.#restoreFocusSelector())
  }

  hydrate(queryState = new TableQueryState(window.location.search)) {
    const params = queryState.toSearchParams()
    this.fieldTargets.forEach((field) => {
      const values = params.getAll(this.#paramName(field))
      if (field.type === "radio") {
        // A radio whose value is "" stands for "no filter": it is the checked
        // one when the param is absent from the URL, and also when the URL
        // carries a value no radio in the group offers (the server ignored it).
        const matched = values.length > 0 && this.#radioGroup(field).some((radio) => values.includes(radio.value))
        field.checked = matched ? values.includes(field.value) : field.value === ""
      } else if (field.type === "checkbox") {
        field.checked = values.includes(field.value)
      } else if (field.multiple) {
        Array.from(field.options).forEach((option) => {
          option.selected = values.includes(option.value)
        })
      } else {
        field.value = values[0] || ""
      }
    })
  }

  #navigate(restoreFocusSelector) {
    const queryState = this.#queryState()
    const go = () =>
      this.navigator.navigate(queryState, {
        history: "push",
        reason: "filter",
        preserveScroll: { x: true, y: false },
        restoreFocusSelector
      })

    const sheet = this.#sheet()
    // No live instance (a Turbo snapshot restored a sheet that looks open)
    // means nobody will ever fire `hidden`; navigate straight away.
    const instance = sheet && Offcanvas.getInstance(sheet)
    if (!sheet || !instance || !this.#sheetMoving(sheet)) return go()

    sheet.addEventListener("hidden.bs.offcanvas", go, { once: true })
    this.#hideSheet(sheet, instance)
  }

  // Open, or still animating open — a pick during the show transition (fast
  // taps, Cypress) must wait for `shown` before it can be hidden cleanly.
  #sheetMoving(sheet) {
    return sheet.classList.contains("show") || sheet.classList.contains("showing")
  }

  #hideSheet(sheet, instance) {
    if (sheet.classList.contains("showing")) {
      sheet.addEventListener("shown.bs.offcanvas", () => instance.hide(), { once: true })
    } else {
      instance.hide()
    }
  }

  // Two radio groups for the same URL param on one page (the A–Z row and its
  // mobile sheet) must not share a `name`, or the browser merges them into one
  // group; `data-table-filter-navigation-param` maps a field to its param.
  #radioGroup(field) {
    return this.fieldTargets.filter((other) => other.type === "radio" && other.name === field.name)
  }

  #paramName(field) {
    return field.dataset.tableFilterNavigationParam || field.name
  }

  #sheet() {
    return this.element.closest(".offcanvas")
  }

  #restoreFocusSelector() {
    if (this.hasRestoreFocusValue && this.restoreFocusValue) return this.restoreFocusValue
    const active = document.activeElement
    if (active?.id && this.fieldTargets.includes(active)) return `#${CSS.escape(active.id)}`
    return null
  }

  #queryState() {
    const managedParams = this.hasManagedParamsValue
      ? this.managedParamsValue
      : [...new Set(this.fieldTargets.map((field) => this.#paramName(field)).filter(Boolean))]
    const changes = Object.fromEntries(managedParams.map((key) => [key, null]))

    this.fieldTargets.forEach((field) => {
      const name = this.#paramName(field)
      if (!name || field.disabled) return
      const values = this.#fieldValues(field)
      if (values.length === 0) return
      changes[name] = [...(Array.isArray(changes[name]) ? changes[name] : []), ...values]
    })
    return new TableQueryState(window.location.search).patch(changes)
  }

  #fieldValues(field) {
    if ((field.type === "checkbox" || field.type === "radio") && !field.checked) return []
    if (field.multiple)
      return Array.from(field.selectedOptions)
        .map((option) => option.value)
        .filter(Boolean)
    const value = field.value?.trim()
    return value ? [value] : []
  }

  #clearField(field) {
    if (field.type === "checkbox" || field.type === "radio") {
      field.checked = false
    } else if (field.multiple) {
      Array.from(field.options).forEach((option) => {
        option.selected = false
      })
    } else {
      field.value = ""
    }
  }
}
